import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useState, useMemo, useCallback} from 'react';

import useHydratedStructuredTableData from 'notebook/structured-tables/hooks/useHydratedStructuredTableData';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import {OutsideNotebookContext} from 'notebook/structured-tables/StructuredTableOutsideNotebookConstants';
import MediocreWrapper from 'notebook/tables/core-ui/MediocreWrapper';
import {EvaluatedCell, EvaluatedTable} from 'notebook/tables/excess/Evaluator';
import {evaluateTablesWithResolutions} from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import {TypedValue} from 'notebook/tables/TableConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY, GRAY_DARKER, GRAY_LIGHTER} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {StructuredTableFormControls} from './StructuredTableForm';
import {buildComputedTable} from './StructuredTableOutsideNotebook';

async function buildAsyncComputedTable<T extends UnknownStructuredTable>(
  structuredTable: T,
  oldComputedTable: EvaluatedTable | null
): Promise<EvaluatedTable> {
  const adapter = getStructuredTableAdapters()[structuredTable.kind];
  const {computedTables} = await evaluateTablesWithResolutions({
    fullTableMap: {[structuredTable.cid]: adapter.toTable(structuredTable)},
    hasFormulasDisabled: false,
    selectors: [],
  });
  return computedTables[structuredTable.cid];
}

export function useAsyncComputedTable<T extends UnknownStructuredTable>(structuredTable: T): EvaluatedTable {
  const [computedTable, setComputedTable] = useState<EvaluatedTable>(
    buildComputedTable(structuredTable, null)
  );
  const computedTablePromise = useCallback(
    () => buildAsyncComputedTable(structuredTable, null),
    [structuredTable]
  );
  // First compute the table with the values available now
  useMemo(() => {
    setComputedTable(buildComputedTable(structuredTable, null));
  }, [structuredTable]);
  // Then rerender if an async promise could change their value (snapshot field previews)
  useEffect(() => {
    computedTablePromise().then((newComputedTable) => {
      setComputedTable(newComputedTable);
    });
  }, [computedTablePromise]);

  return computedTable;
}

interface StructuredTableOutsideNotebookProps {
  structuredTable: UnknownStructuredTable;
  cid: string;
  locked?: boolean;
  updateHandler: (
    structuredTable: UnknownStructuredTable,
    overrideArchivePurposeByRow?: Array<string | null | undefined>
  ) => void;
  onComputedTableChanged?: (computedTable: Array<Array<EvaluatedCell>>) => void;
  disableMaximizedTables?: boolean;
  hideFormulaBox?: boolean;
  isMaximized?: boolean;
  setMaximizedTable?: (cid: string) => void;
  hasPrimarySamples?: boolean;
  includeUnsubmittedStatus?: boolean;
  hideLeftHeaderControls?: boolean;
  outsideNotebookContext?: OutsideNotebookContext | null;
}

export default function StructuredTableFormOutsideNotebook(
  props: StructuredTableOutsideNotebookProps
): JSX.Element {
  const {
    structuredTable,
    updateHandler,
    onComputedTableChanged,
    locked = false,
    hasPrimarySamples = true,
    includeUnsubmittedStatus = true,
  } = props;
  const adapter = getStructuredTableAdapters()[structuredTable.kind];
  const computedTable = useAsyncComputedTable(structuredTable);

  const {table: originalTable, staleCellIndices} = useHydratedStructuredTableData({
    adapter,
    structuredTable,
    computedTable,
    validationErrors: [],
    options: {hasPrimarySamples, includeUnsubmittedStatus},
  });
  let addButtonLabel = 'Add';
  if (adapter.getRowDisplayName !== undefined) {
    addButtonLabel += ` ${adapter.getRowDisplayName(structuredTable)}`;
  }
  const table = locked
    ? {
        ...originalTable,
        table: originalTable.table.map((row) => row.map((cell) => ({...cell, locked: true}))),
      }
    : originalTable;

  useEffect(() => {
    if (onComputedTableChanged) {
      onComputedTableChanged(computedTable);
    }
  }, [onComputedTableChanged, computedTable]);

  function updateTypedValue(rowIndex: number, columnIndex: number, typedValue: TypedValue | null): void {
    const sourceRow = structuredTable.rows[rowIndex];
    const row = {...sourceRow, cells: [...sourceRow.cells]};
    const sourceCell = row.cells[columnIndex];
    row.cells[columnIndex] = {
      ...sourceCell,
      typedValue,
      text: '',
    };
    const newTable = adapter.fromTable({...adapter.toTable(structuredTable)});
    newTable.rows = newTable.rows.map((oldrow, idx) => {
      if (idx === rowIndex) {
        return {...row};
      }
      return oldrow;
    });
    updateHandler(newTable);
    if (props.onComputedTableChanged) {
      props.onComputedTableChanged(computedTable);
    }
  }

  const deleteFormRow = (rowIndex: number): void => {
    const newTable = adapter.fromTable({...adapter.toTable(structuredTable)});
    newTable.rows.splice(rowIndex, 1);
    updateHandler(newTable);
  };

  const addFormRow = (): void => {
    const newTable = adapter.fromTable({...adapter.toTable(structuredTable)});
    const newRow = adapter.getBlankRow(table.metadata, newTable.rows.length + 1, newTable.columns);

    newTable.rows.push(newRow);
    updateHandler(newTable);
  };

  return (
    <MediocreWrapper>
      {structuredTable.rows.map((row, index) => (
        <div
          className={classNames('structuredTableFormOutsideNotebook-section', css(styles.formBackground))}
          key={index}
        >
          <StructuredTableFormControls
            table={table}
            computedTable={computedTable}
            staleCellIndices={staleCellIndices}
            updateOneTypedValue={(...args) => {
              updateTypedValue(index, ...args);
            }}
            locked={locked}
            adapter={adapter}
            rowIndex={index}
          />
          {!props.locked && (
            <div
              className={classNames(
                'structuredTableFormOutsideNotebook-removeButton',
                css(styles.removeForm)
              )}
              {...getTestProps({
                component: 'StructureTableFormOutsideNotebook',
                element: 'removeButton',
              })}
              onClick={() => {
                deleteFormRow(index);
              }}
            >
              <BenchlingIcon icon="delete" altText="Delete" />
            </div>
          )}
        </div>
      ))}
      {!props.locked && (
        <Button
          className={css(styles.addForm)}
          buttonStyle={ButtonStyle.TEXT}
          size={ButtonSize.SMALL}
          icon="compose"
          text={addButtonLabel}
          onClick={addFormRow}
          dataTestAttrs={{
            key: 'addButton',
            component: 'Button',
          }}
        />
      )}
    </MediocreWrapper>
  );
}

const styles = StyleSheet.create({
  formBackground: {
    backgroundColor: GRAY_LIGHTER,
    borderRadius: '4px',
    marginBottom: '12px',
    padding: '6px',
    position: 'relative',
    display: 'flex',
  },
  removeForm: {
    cursor: 'pointer',
    color: GRAY,
    margin: '6px',
    alignSelf: 'flex-start',
    ':hover': {
      color: GRAY_DARKER,
    },
  },
  addForm: {
    marginBottom: '10px',
  },
});
